import { MenuItem, Select, CircularProgress } from '@mui/material';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useEffect, useState, useMemo } from 'react';
import { editTicketData } from '../../../../redux/slices/tickets';
import { useDispatch, useSelector } from '../../../../redux/store';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import { getUserFacilityTicketCategoryPermissions } from '../../../../api/tickets';

TicketCategory.propTypes = {
  ticketId: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  selectedTicketCategory: PropTypes.number,
  setSelectedTicketCategory: PropTypes.func,
  addOtherCategory: PropTypes.bool,
}

export default function TicketCategory({ ticketId, selectedTicketCategory, setSelectedTicketCategory, addOtherCategory }) {
  const dispatch = useDispatch();

  const { enqueueSnackbar } = useSnackbar();

  const { mainTicket, ticketCategories } = useSelector((state) => state.tickets.data.ticketInfoById[ticketId]) || {};
  const { data: { ticketCategories: generalTicketCategories } } = useSelector((state) => state.tickets);
  const [filteredTicketCategories, setFilteredTicketCategories] = useState([]);
  const user = useSelector((state) => state.user.user);
  const { orderById } = useSelector((state) => state.orders || {});
  const { id = '' } = useParams();
  const [isTicketCategoryLoading, setIsTicketCategoryLoading] = useState(false);

  const categories = useMemo(() => {
    const cats = ticketCategories || generalTicketCategories || [];   
    if (!addOtherCategory) return cats;
    if (cats?.length > 0 && !cats.some(val => val.value === 0)) return [...cats, { value: 0, label: "Other" }];
    return cats;
  }, [ticketCategories, generalTicketCategories, addOtherCategory]);

  useEffect(() => {
    if (mainTicket?.facilityId || orderById[id]?.facilityId) {
      if (user.roles.includes('Admin') || !!ticketCategories) setFilteredTicketCategories(categories)
      else {
        getUserFacilityTicketCategoryPermissions(mainTicket?.facilityId || orderById[id]?.facilityId).then(response => {
          setFilteredTicketCategories([...categories.filter(cat => response?.data?.includes(cat.value))]);
        });
      }
    }
  }, [mainTicket, user.roles, orderById, id, categories])

  const editTicketCategory = async (value) => {
    if(mainTicket?.id) {
      setIsTicketCategoryLoading(true);
      const response = await dispatch(editTicketData({ Id: mainTicket?.id, value, updateType: "TicketCategoryId" }));
      if (response) {
        setSelectedTicketCategory(value);
        enqueueSnackbar('Category updated successfully', { variant: 'success' });
      }
      else enqueueSnackbar('Category failed to update', { variant: 'error' });
      setIsTicketCategoryLoading(false);
    }
    else {
      setSelectedTicketCategory(value);
    }
  };

  return (
    !mainTicket?.locked &&
    filteredTicketCategories?.length > 0 && (
      <Select
        value={filteredTicketCategories.find((cat) => cat.value === selectedTicketCategory)?.value || 'catTitle'}
        onChange={(_, newValue) => editTicketCategory(newValue?.props?.value)}
        displayEmpty
        disabled={isTicketCategoryLoading}
        label="Category"
        labelId="demo-simple-select-label"
        renderValue={() =>
          isTicketCategoryLoading ? (
            <CircularProgress size={20} sx={{ verticalAlign: 'middle' }} />
          ) : (
            filteredTicketCategories.find((cat) => cat.value === selectedTicketCategory)?.label || 'Select Category'
          )
        }
        sx={{
          '& .MuiSelect-select': {
            paddingLeft: 0,
          },
          maxHeight: '26px',
          color: (theme) => theme.palette.info.main,
          maxWidth: '100%',
          width: 'fit-content',
          fontSize: '0.85rem',
          '& .MuiSelect-icon': {
            color: (theme) => theme.palette.info.main,
          },
          '&.Mui-disabled .MuiSelect-icon': {
            color: (theme) => theme.palette.info.main,
          },
        }}
      >
        <MenuItem disabled sx={{ display: 'hidden' }} value="catTitle">
          Select Category
        </MenuItem>
        {Array.isArray(filteredTicketCategories) &&
          filteredTicketCategories.map((t) => (
            <MenuItem key={t.value} value={t.value}>
              {t.label}
            </MenuItem>
          ))}
      </Select>
    )
  );
}
